import { css, html } from "lit";
import { z } from "zod";
import { checkNotNil } from "../check-not-nil.js";
import { attributeProperty } from "../property.js";
import type { ParserType } from "../schemas/parser.js";
import { hostStyles } from "../styles/host.styles.js";
import { WmElement } from "./element.js";

// TODO: should not be named "plain", as this conflicts with how Web Awesome
// implements plain (they keep the padding). It should be something else...
// like "minimal" or something.
const Schema = z.enum(["plain", "outlined"]);
type Appearance = ParserType<typeof Appearance>;
const Appearance = attributeProperty("appearance", Schema, {
  defaultValue: "outlined",
});

type Label = ParserType<typeof Label>;
const Label = attributeProperty("label", z.string(), { defaultValue: "" });

export class WmOutput extends WmElement {
  static readonly TAG_NAME = "wm-output";

  static properties = {
    label: Label,
    appearance: Appearance,
  };

  static styles = [
    hostStyles,
    // Custom CSS that follows Web Awesome's form control layout.
    css`
      .container {
        display: flex;
        flex-direction: column;
      }

      label {
        color: var(--wa-form-control-label-color);
        font-weight: var(--wa-form-control-label-font-weight);
        line-height: var(--wa-form-control-label-line-height);
        margin-block-end: 0.5em;
      }

      output {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: start;
        height: var(--wa-form-control-height);
        cursor: text;
        color: var(--wa-form-control-value-color);
        font-size: var(--wa-form-control-value-font-size);
        font-family: inherit;
        font-weight: var(--wa-form-control-value-font-weight);
        line-height: var(--wa-form-control-value-line-height);
        vertical-align: middle;
        width: 100%;
        transition:
          background-color var(--wa-transition-normal),
          border-color var(--wa-transition-normal),
          outline-color var(--wa-transition-fast);
        transition-timing-function: var(--wa-transition-easing);
        background-color: var(--wa-form-control-background-color);
        box-shadow: var(--box-shadow);
        outline: var(--wa-focus-ring-style) var(--wa-focus-ring-width) transparent;
        outline-offset: var(--wa-focus-ring-offset);

        border-color: var(--wa-form-control-border-color);
        border-radius: var(--wa-form-control-border-radius);
        border-style: var(--wa-form-control-border-style);
        border-width: var(--wa-form-control-border-width);
        padding: 0 var(--wa-form-control-padding-inline);
        &:focus {
          outline-color: var(--wa-color-focus);
        }
      }

      /*
      Removing border and padding when set to plain.
      */
      :host([appearance="plain"]) {
        output {
          border-style: none;
          padding: 0;
          &:focus {
            all: unset;
          }
        }
      }
    `,
  ];

  declare readonly label: Label;
  declare readonly appearance: Appearance;

  render() {
    return html`
      <div class="container">
        <label for="output">${this.label}</label>
        <output id="output" tabindex="0" @blur=${this.#handleBlur} @focus=${this.#handleFocus}>
          <slot></slot>
        </output>
      </div>
    `;
  }

  #handleFocus(event: FocusEvent) {
    const target = checkNotNil(event.target);
    const node = target as Node;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection: Selection = checkNotNil(window.getSelection());
    selection.removeAllRanges();
    selection.addRange(range);
  }

  #handleBlur(_: FocusEvent) {
    window.getSelection()?.removeAllRanges();
  }
}

customElements.define(WmOutput.TAG_NAME, WmOutput);
